import { useState } from "react";
import { X } from "lucide-react";
import IconButton from "@/common/components/IconButton";
import { Popover, PopoverContent, PopoverTrigger } from "@/common/components/ui/popover";
import Button from "@/common/components/Button";
import CardItem from "./CardItem";
import useCardReader from "../../hooks/useCardReader";
import { useCardMarkAsValidated } from "../../hooks/useCardMarkAsValidated";
import { CardData } from "../../types/card.type";
import { useQueryClient } from "@tanstack/react-query";

const ValidateCardsPopover = () => {
  const queryClient = useQueryClient();

  const [baseError, setBaseError] = useState<string[] | null>(null);

  const [open, onOpenChange] = useState(false);
  const [validatedCards, setValidatedCards] = useState<CardData[]>([]);

  useCardReader({
    enabled: open,
    onRead: (cardTrackString) => {
      cardMarkAsValidated(cardTrackString);
    }});

  const { mutate: cardMarkAsValidated } = useCardMarkAsValidated({
    onSuccess(response) {
      const card = response.data.data as CardData;

      setValidatedCards((prev) => [...prev, card]);

      setBaseError([]);

      queryClient.invalidateQueries({ queryKey: ['clientCardsInFabrication'] });
    },
    onError(error) {
      if (error?.response?.data?.errors?.base?.length) {
        setBaseError(error.response.data.errors.base);
      }
    }
  });

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger>
        <Button className="text-nowrap" operationKlassToCheckPermission="Admin::Client::Cards::MarkAsValidated">
          Validar cartões
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="-mt-12 mr-6 w-[19.0625rem] bg-[#2C2C2C] text-white-800 p-0 rounded-lg border-none shadow-[0px_4px_4px_0px_rgba(0,_0,_0,_0.251)]">
        <div className="px-4 py-3 flex justify-between items-center gap-x-2 bg-white-200 rounded-t-lg">
          <div className="flex items-center gap-1">
            <p className="text-md">Validação de cartões</p>
          </div>

          <IconButton
            onClick={() => onOpenChange(false)}
            Icon={<X strokeWidth={1} />}
            className="text-danger"
          />
        </div>

        <div className="max-h-[50dvh] overflow-hidden">
          <div className="flex flex-col justify-center items-center">
            <div className="flex flex-col gap-2">
              <div className="p-4">
                <p className="text-xs">Passe os cartões impressos no leitor de cartões para validação, após validar o cartão o status do mesmo será alterado para Validado.</p>
              </div>

              {baseError && (
                <div className="p-4 pt-0 -mt-2">
                  {baseError.map((error, i) => <p className="text-xs text-danger-soft" key={`validate-card-error-${i}`}>{error}</p>)}
                </div>
              )}
            </div>

            {validatedCards.length > 0 ? (
              <div className="h-[calc(50dvh-80px)] w-full pb-2 px-2 flex flex-col items-center gap-2 overflow-y-auto">
                {validatedCards.map((card, index) => <CardItem key={card.id} index={validatedCards.length - index} card={card} />)}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div>
                  <img src="/search.gif" className="shrink-0 w-56" />
                </div>
                <div className="-mt-16 mb-16 p-8 space-y-2">
                  <p className="text-sm font-bold text-center">Você ainda não leu nenhum cartão.</p>
                  <p className="text-xs text-center">Inicie uma leitura do cartão para validação do mesmo.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ValidateCardsPopover;
